import React, { useState, useEffect } from 'react';
import { getAllStaff, createStaff, updateStaff, deleteStaff } from '../services/api';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Plus, Users, UserCheck, Loader2, Edit, Trash2 } from 'lucide-react';

const StaffManagementPage = () => {
  const { t } = useLanguage();
  const { user } = useAuth();
  const [staffList, setStaffList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [editingStaff, setEditingStaff] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    position: '',
    department: '',
    phone: '',
    email: '',
  });

  useEffect(() => {
    loadStaff();
  }, []);

  const loadStaff = async () => {
    try {
      const data = await getAllStaff();
      setStaffList(data);
    } catch (error) {
      toast.error(t('failedToLoad'));
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      position: '',
      department: '',
      phone: '',
      email: '',
    });
    setEditingStaff(null);
  };

  const handleOpenCreate = () => {
    resetForm();
    setShowCreateDialog(true);
  };

  const handleOpenEdit = (staff) => {
    setFormData({
      name: staff.name,
      position: staff.position,
      department: staff.department || '',
      phone: staff.phone || '',
      email: staff.email || '',
    });
    setEditingStaff(staff);
    setShowCreateDialog(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.position) {
      toast.error('Name and position are required');
      return;
    }

    setIsSubmitting(true);
    try {
      if (editingStaff) {
        await updateStaff(editingStaff.id, formData);
        toast.success(t('staffUpdated'));
      } else {
        await createStaff(formData);
        toast.success(t('staffCreated'));
      }
      setShowCreateDialog(false);
      resetForm();
      loadStaff();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('errorOccurred'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (staffId) => {
    if (!window.confirm('Are you sure you want to deactivate this staff member?')) {
      return;
    }

    try {
      await deleteStaff(staffId);
      toast.success(t('staffDeleted'));
      loadStaff();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('errorOccurred'));
    }
  };

  // Only admin can access
  if (user?.role !== 'admin') {
    return (
      <div className="flex flex-col items-center justify-center h-96">
        <h2 className="text-2xl font-bold text-foreground">{t('accessDenied')}</h2>
        <p className="text-muted-foreground mt-2">{t('noPermission')}</p>
      </div>
    );
  }

  const activeStaff = staffList.filter((s) => s.is_active);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="staff-management-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl font-bold font-heading text-foreground">{t('staffManagement')}</h1>
          <p className="text-muted-foreground mt-2">{t('manageStaff')}</p>
        </div>
        <Button onClick={handleOpenCreate} className="rounded-full" data-testid="add-staff-button">
          <Plus className="mr-2 h-4 w-4" />
          {t('createStaff')}
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">{t('totalStaff')}</p>
                <p className="text-3xl font-bold font-heading mt-2">{staffList.length}</p>
              </div>
              <div className="p-3 rounded-xl bg-primary/10">
                <Users className="h-6 w-6 text-primary" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">{t('activeStaff')}</p>
                <p className="text-3xl font-bold font-heading mt-2">{activeStaff.length}</p>
              </div>
              <div className="p-3 rounded-xl bg-green-100">
                <UserCheck className="h-6 w-6 text-green-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Staff List */}
      <Card>
        <CardHeader>
          <CardTitle className="font-heading">All Staff Members</CardTitle>
        </CardHeader>
        <CardContent>
          {staffList.length > 0 ? (
            <div className="space-y-3">
              {staffList.map((staff) => (
                <div
                  key={staff.id}
                  className={`flex items-center justify-between p-4 rounded-lg border ${
                    staff.is_active ? 'bg-background' : 'bg-muted/50 opacity-60'
                  }`}
                  data-testid={`staff-row-${staff.id}`}
                >
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-1">
                      <h3 className="font-semibold font-heading">{staff.name}</h3>
                      <Badge variant={staff.is_active ? 'default' : 'secondary'}>
                        {staff.is_active ? 'Active' : 'Inactive'}
                      </Badge>
                    </div>
                    <div className="flex flex-wrap gap-4 text-sm text-muted-foreground">
                      <span>{staff.position}</span>
                      {staff.department && <span>• {staff.department}</span>}
                      {staff.phone && <span>• {staff.phone}</span>}
                      {staff.email && <span>• {staff.email}</span>}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleOpenEdit(staff)}
                      data-testid={`edit-staff-${staff.id}`}
                    >
                      <Edit className="h-4 w-4" />
                    </Button>
                    {staff.is_active && (
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleDelete(staff.id)}
                        className="text-destructive hover:text-destructive"
                        data-testid={`delete-staff-${staff.id}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12 text-muted-foreground">
              <Users className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>No staff members yet</p>
              <Button variant="outline" onClick={handleOpenCreate} className="mt-4">
                {t('createStaff')}
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create/Edit Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-heading">
              {editingStaff ? 'Edit Staff Member' : t('createNewStaff')}
            </DialogTitle>
            <DialogDescription>
              {editingStaff ? 'Update staff member details' : t('addNewStaff')}
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="name">{t('staffName')} *</Label>
              <Input
                id="name"
                data-testid="staff-name-input"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
                placeholder="Enter staff name"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="position">{t('staffPosition')} *</Label>
              <Input
                id="position"
                data-testid="staff-position-input"
                value={formData.position}
                onChange={(e) => setFormData({ ...formData, position: e.target.value })}
                required
                placeholder="e.g., Field Worker, Supervisor, Foreman"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="department">{t('staffDepartment')}</Label>
              <Input
                id="department"
                data-testid="staff-department-input"
                value={formData.department}
                onChange={(e) => setFormData({ ...formData, department: e.target.value })}
                placeholder="e.g., Field Operations, Maintenance"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="phone">{t('staffPhone')}</Label>
                <Input
                  id="phone"
                  data-testid="staff-phone-input"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  placeholder="+56 9 1234 5678"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="email">{t('staffEmail')}</Label>
                <Input
                  id="email"
                  type="email"
                  data-testid="staff-email-input"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="staff@ajl.com"
                />
              </div>
            </div>

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowCreateDialog(false)}
              >
                {t('cancel')}
              </Button>
              <Button type="submit" disabled={isSubmitting} data-testid="submit-staff-button">
                {isSubmitting ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    {t('saving')}
                  </>
                ) : editingStaff ? (
                  t('update')
                ) : (
                  t('create')
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default StaffManagementPage;
